import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl } from '../utils/helpers';
import { Sparkles, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Services = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Check for category in URL query params
    const params = new URLSearchParams(location.search);
    const categoryId = params.get('category');
    if (categoryId) {
      console.log('Setting category from URL:', categoryId);
      setSelectedCategory(categoryId);
    } else {
      console.log('No category in URL, resetting to null');
      setSelectedCategory(null);
    }
  }, [location.search]);

  useEffect(() => {
    fetchServices();
  }, [location.search, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/service-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      
      // Read category directly from URL to avoid stale state
      const params = {};
      const urlParams = new URLSearchParams(location.search);
      const categoryFromUrl = urlParams.get('category');
      
      if (categoryFromUrl) {
        params.category_id = categoryFromUrl;
      }
      
      console.log('Fetching services with params:', params);
      const response = await axios.get(`${API_URL}/services`, { params });
      console.log('Received services:', response.data.length);
      
      // Filter by search query on frontend
      let filtered = response.data;
      if (searchQuery) {
        filtered = filtered.filter(service => 
          service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          service.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      setServices(filtered);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="section-title text-4xl md:text-5xl mb-4">
            <span>Szolgáltatások</span>
          </h1>
          <p className="text-gray-300 text-lg">Válaszd ki az Önnek megfelelő szolgáltatást</p>
        </div>

        {/* Search & Filter */}
        <div className="glass rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Keresés..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  navigate('/szolgaltatasok');
                }}
                className={`px-4 py-2 rounded-lg transition ${
                  !selectedCategory
                    ? 'bg-yellow-200 text-black'
                    : 'bg-white/5 text-white hover:bg-white/10'
                }`}
              >
                Összes
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    navigate(`/szolgaltatasok?category=${category.id}`);
                  }}
                  className={`px-4 py-2 rounded-lg transition ${
                    selectedCategory === category.id
                      ? 'bg-yellow-200 text-black'
                      : 'bg-white/5 text-white hover:bg-white/10'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200"></div>
            <p className="mt-4 text-gray-400">Betöltés...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 glass rounded-2xl">
            <Sparkles className="w-16 h-16 text-yellow-200/50 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Nincs elérhető szolgáltatás</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/szolgaltatasok/${service.id}`}
                className="group glass rounded-2xl overflow-hidden hover:border-yellow-200/50 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Service Image */}
                <div className="relative h-56 overflow-hidden bg-gradient-to-br from-purple-900/20 to-black">
                  {service.images && service.images.length > 0 ? (
                    <img
                      src={formatImageUrl(service.images[0])}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles className="w-16 h-16 text-yellow-200/30" />
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-black/60 backdrop-blur-sm border border-yellow-200/30 rounded-full text-xs text-yellow-200">
                      {categories.find(c => c.id === service.category_id)?.name || 'Szolgáltatás'}
                    </span>
                  </div>
                </div>

                {/* Service Info */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-yellow-200 transition">
                    {service.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-yellow-200">
                      {formatPrice(service.price)}
                    </span>
                    <span className="text-sm text-gray-400 group-hover:text-yellow-200 transition">
                      Részletek →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Services;
